import React, { useState } from 'react';

import { useLocalization } from '@progress/kendo-react-intl';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Error, Label } from "@progress/kendo-react-labels";
import {
    emailValidator,
    requiredValidator,
    phoneValidator,
    address1Validator,
    cityValidator,
    zipValidator,
    nameValidator,
    minLengthValidator,
} from '../validators';
import { states } from '../resources/states';
import '../styles/_elig_form.scss';

// @ts-ignore
import PrivacyBanner from 'react-ford-ccpa-banner';

import { AppContext } from './../AppContext'
import { useServices } from 'src/services';
import { Input } from 'src/components/form/Input';
import { TextArea } from 'src/components/form/TextArea';
import { MaskedTextBox } from 'src/components/form/MaskedTextBox';
import { useHistory } from 'react-router';
import { DropDownList } from 'src/components/form/DropDownList';
import { DatePicker } from 'src/components/form/DatePicker';
import { TimePicker } from 'src/components/form/TimePicker';
import moment from 'moment';
import { Checkbox } from 'src/components/form/Checkbox';
import { Loader } from '@progress/kendo-react-indicators';
import { FieldWrapper } from '@progress/kendo-react-form';
// import { useStores } from 'src/stores';
const EligibilityFormScreen = () => {
    const { ...formValues } = React.useContext(AppContext);
    const localizationService = useLocalization();
    const { language } = localizationService;

    const [formError, setFormError] = useState<string>();
    const { d4ur } = useServices();
    let history = useHistory();
    // const { login } = useStores();
    // const isHispanic = login.dealer?.hispanic;

    const [processing, setProcessing] = React.useState<boolean>(false);
    const [displayOtherAddressForm, setDisplayOtherAddressForm] = React.useState<boolean>(false);
    const [timeValidationError, setTimeValidationError] = React.useState<string>('');

    // Clear time validation error when form error is cleared
    React.useEffect(() => {
        if (!formError) {
            setTimeValidationError('');
        }
    }, [formError]);

    const onSubmit = React.useCallback(
        async (dataItem) => {
            setProcessing(true);

            // Validate times before formatting
            if (dataItem.start_time && dataItem.end_time) {
                const startMoment = moment(dataItem.start_time);
                const endMoment = moment(dataItem.end_time);
                if (endMoment.isSameOrBefore(startMoment)) {
                    setFormError('End time must be after start time.');
                    setTimeValidationError('End time must be after start time.');
                    setProcessing(false);
                    return;
                }
            }
            
            // Clear time validation error if validation passes
            setTimeValidationError('');

            dataItem.season_id = 11; // TODO: season should be dynamic

            // format dates properly
            if (dataItem.start_date) {
                dataItem.start_date = moment(dataItem.start_date).format('YYYY-MM-DD');
            }

            // format time properly
            if (dataItem.start_time) {
                dataItem.start_time = moment(dataItem.start_time).format('HH:mm:ss');
            }
            if (dataItem.end_time) {
                dataItem.end_time = moment(dataItem.end_time).format('HH:mm:ss');
            }

            // other json data
            dataItem.event_custom_data = {
                additional_schools: dataItem.additional_schools,
                kit_location: dataItem.kit_location,
                event_location_type: dataItem.event_location_type,
            };

            if (!displayOtherAddressForm) {
                dataItem.event_address = '';
                dataItem.event_city = '';
                dataItem.event_state = '';
                dataItem.event_postal_code = '';
            }

            const eventRequest: D4UREvent = dataItem;

            const call: any = await d4ur.saveRequest(eventRequest);
            if (call.result.errors || call.result.error_message) {
                console.log('[EligibilityFormScreen]', 'Event Request Error');
                setFormError(call.result.errors ? call.result.join('\n') : call.result.error_message);
                setProcessing(false);
            } else {
                console.log('[EligibilityFormScreen]', 'event requested');
                history.push('/events');
            }
        },
        [d4ur, displayOtherAddressForm, history]
    );

    const ref: any = React.useRef(null);
    const dateRef: any = React.useRef(null);

    const handleOnWheel = (event: any) => {
        event.stopPropagation();
    };

    React.useEffect(() => {
        ref?.current?.element.addEventListener('wheel', handleOnWheel);
        dateRef?.current?.element.addEventListener('wheel', handleOnWheel);

        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            ref?.current?.element.removeEventListener('wheel', handleOnWheel);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            dateRef?.current?.element.removeEventListener('wheel', handleOnWheel);
        };
    }, []);


    console.log(' date??', moment().add(10, 'days').toDate());

    return (
        <>
            <div id="Eligibility" className="eligibility-page">
                <div className="eligibility-content">
                    <div className="form-container">
                        <div className="card-component">
                            <h3 className="request-title">
                                {localizationService.toLanguageString('custom.eligibilityRequest', 'eligibilityRequest')}
                            </h3>
                            <p className="request-text">
                                Please fill out the form below, providing information about the event date and organization. Please make sure that you have contacted your organization(s) and have agreed on an event date prior to submitting this form.
                                Please complete all fields of the event request form. For official rules and details please read the <a href="/terms-eligibility" target='_blank'>Terms & Conditions</a>. Press the "Submit Event Request" button when complete.
                            </p>
                            {processing ? (
                                <div style={{ textAlign: 'center' }}>
                                    <Loader type="infinite-spinner" size="large" />
                                </div>
                            ) : (
                                <Form
                                    onSubmit={onSubmit}
                                    initialValues={{
                                        ...formValues,
                                        tax_id: '',
                                    }}
                                    render={(formRenderProps) => (
                                        <FormElement horizontal={true} style={{ maxWidth: '100%' }}>
                                            {formError && <Error>{formError}</Error>}
                                            <FieldWrapper>
                                                <Label >{'Enrollment Period:'}</Label>
                                                <div className={'k-form-field-wrap'}>
                                                   <Label style={{ paddingTop: '5px', fontFamily: 'FordF1-Semibold'}}>Spring/Fall 2026</Label>
                                                </div>
                                            </FieldWrapper>
                                            <Field
                                                id={'start_date'}
                                                name={'start_date'}
                                                childRef={dateRef}
                                                label={localizationService.toLanguageString('custom.event_date', 'event_date')}
                                                format="MM/dd/yyyy"
                                                // if today + 10 days is before the first event date, then min date is the first event date. Otherwise, if today is inside the event date range, then min date is 10 days from today.
                                                min={moment().add(10, 'days').isBefore('2026-04-01') ? moment('2026-04-01', 'YYYY-MM-DD').toDate() : moment().add(10, 'days').toDate()}
                                                max={moment('2026-11-30', 'YYYY-MM-DD').toDate()}
                                                validator={requiredValidator}
                                                component={DatePicker}
                                                readOnly={true}
                                                hint='Your event date must be between April 1st and November 30th and be at least 10 days away.'
                                            />
                                            <Field
                                                id={'start_time'}
                                                name={'start_time'}
                                                label={'Event Start Time'}
                                                format="hh:mm a"
                                                validator={requiredValidator}
                                                component={TimePicker}
                                                hint='Please select the start time for your event. (You will be able to change/update your time in your Event Portal.)'
                                                onChange={(e) => {
                                                    const startTime = e.value;
                                                    const endTime = formRenderProps.valueGetter('end_time');
                                                    if (startTime && endTime) {
                                                        if (moment(endTime).isSameOrBefore(moment(startTime))) {
                                                            setTimeValidationError('End time must be after start time.');
                                                        } else {
                                                            setTimeValidationError('');
                                                        }
                                                    } else {
                                                        setTimeValidationError('');
                                                    }
                                                }}
                                            />
                                            <Field
                                                id={'end_time'}
                                                name={'end_time'}
                                                label={'Event End Time'}
                                                format="hh:mm a"
                                                validator={requiredValidator}
                                                component={TimePicker}
                                                hint='Please select the end time for your event. (You will be able to change/update your time in your Event Portal.)'
                                                onChange={(e) => {
                                                    const endTime = e.value;
                                                    const startTime = formRenderProps.valueGetter('start_time');
                                                    if (startTime && endTime) {
                                                        if (moment(endTime).isSameOrBefore(moment(startTime))) {
                                                            setTimeValidationError('End time must be after start time.');
                                                        } else {
                                                            setTimeValidationError('');
                                                        }
                                                    } else {
                                                        setTimeValidationError('');
                                                    }
                                                }}
                                            />
                                            {timeValidationError && (
                                                <div style={{ 
                                                    color: '#d32f2f', 
                                                    fontSize: '14px', 
                                                    padding: '8px 12px',
                                                    backgroundColor: '#ffebee',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ef5350',
                                                    marginTop: '-10px',
                                                    marginBottom: '10px'
                                                }}>
                                                    {timeValidationError}
                                                </div>
                                            )}
                                            <Field
                                                id={'event_location_type'}
                                                name={'event_location_type'}
                                                data={['The Organization', 'The Dealership', 'Other']}
                                                label={`Event Location`}
                                                validator={requiredValidator}
                                                hint={`Please select the option that best describes the location of your event. Please note that your event location must be held within your dealership's PMA/locality zone. Past approval of events held at locations outside of your dealership's PMA/locality does not guarantee future event approval in the same location.`}
                                                component={DropDownList}
                                                onChange={(e) => {
                                                    if (e.target.value === 'Other') {
                                                        setDisplayOtherAddressForm(true)
                                                    } else {
                                                        setDisplayOtherAddressForm(false)
                                                    }
                                                }}
                                            />
                                            <Field
                                                id={'agree_pma'}
                                                name={'agree_pma'}
                                                label={`${localizationService.toLanguageString('custom.pma', 'terms')}`}
                                                checkboxText={`${localizationService.toLanguageString('custom.agree_pma_terms', 'agree_pma_terms')}`}
                                                validator={requiredValidator}
                                                required={true}
                                                component={Checkbox}
                                            />
                                            { displayOtherAddressForm && 
                                            <> 
                                                <Field
                                                id={'event_address'}
                                                name={'event_address'}
                                                maxLength={45}
                                                label={localizationService.toLanguageString('custom.eventAddress', 'address')}
                                                validator={address1Validator}
                                                required={true}
                                                component={Input}
                                                />
                                                <Field
                                                    id={'event_city'}
                                                    name={'event_city'}
                                                    maxLength={45}
                                                    label={localizationService.toLanguageString('custom.eventCity', 'city')}
                                                    validator={cityValidator}
                                                    required={true}
                                                    component={Input}
                                                />
                                                <Field
                                                    component={DropDownList}
                                                    id={'event_state'}
                                                    name={'event_state'}
                                                    label={localizationService.toLanguageString('custom.eventState', 'state')}
                                                    data={states}
                                                    validator={requiredValidator}
                                                    required={true}
                                                />
                                                <Field
                                                    id={'event_postal_code'}
                                                    name={'event_postal_code'}
                                                    label={localizationService.toLanguageString('custom.eventZip', 'zip')}
                                                    validator={zipValidator}
                                                    required={true}
                                                    mask="00000"
                                                    component={MaskedTextBox}
                                                /> 
                                            </> }
                                            <Field
                                                id={'name'}
                                                name={'name'}
                                                maxLength={100}
                                                label={localizationService.toLanguageString('custom.charity_name', 'charity_name')}
                                                validator={requiredValidator}
                                                component={Input}
                                            />
                                            <Field
                                                id={'address_1'}
                                                name={'address_1'}
                                                maxLength={45}
                                                label={localizationService.toLanguageString('custom.organizationAddress', 'organizationAddress')}
                                                validator={address1Validator}
                                                required={true}
                                                component={Input}
                                            />
                                            <Field
                                                id={'address_2'}
                                                name={'address_2'}
                                                maxLength={45}
                                                label={localizationService.toLanguageString('custom.address2', 'address2')}
                                                component={Input}
                                            />
                                            <Field
                                                id={'city'}
                                                name={'city'}
                                                maxLength={45}
                                                label={localizationService.toLanguageString('custom.city', 'city')}
                                                validator={cityValidator}
                                                required={true}
                                                component={Input}
                                            />
                                            <Field
                                                component={DropDownList}
                                                id={'state'}
                                                name={'state'}
                                                label={localizationService.toLanguageString('custom.state', 'state')}
                                                data={states}
                                                validator={requiredValidator}
                                                required={true}
                                            />
                                            <Field
                                                id={'postal_code'}
                                                name={'postal_code'}
                                                label={localizationService.toLanguageString('custom.zip', 'zip')}
                                                validator={zipValidator}
                                                required={true}
                                                mask="00000"
                                                component={MaskedTextBox}
                                            />
                                            <div className="contact-fields-grid">
                                                <div className="contact-column">
                                                    <Field
                                                        id={'contact_name'}
                                                        name={'contact_name'}
                                                        maxLength={100}
                                                        label={localizationService.toLanguageString('custom.contact_name', 'Organization Contact Name')}
                                                        validator={nameValidator}
                                                        required={true}
                                                        component={Input}
                                                        hint={`This contact should be present on site during the event.`}
                                                    />
                                                </div>
                                                <div className="contact-column">
                                                    <Field
                                                        id={'contact_phone_number'}
                                                        name={'contact_phone_number'}
                                                        label={localizationService.toLanguageString('custom.org_contact_phone', 'org_contact_phone')}
                                                        validator={phoneValidator}
                                                        required={true}
                                                        mask="000-000-0000"
                                                        placeholder={'e.g.: 000-000-0000'}
                                                        component={MaskedTextBox}
                                                    />
                                                </div>
                                                <div className="contact-column">
                                                    <Field
                                                        id={'contact_email'}
                                                        name={'contact_email'}
                                                        type={'email'}
                                                        maxLength={100}
                                                        placeholder={'e.g.: peter@gmail.com'}
                                                        label={localizationService.toLanguageString('custom.org_contact_email', 'org_contact_email')}
                                                        validator={emailValidator}
                                                        required={true}
                                                        component={Input}
                                                    />
                                                </div>
                                            </div>
                                            <p style={{ 
                                                fontSize: '13px', 
                                                marginTop: '0px', 
                                                marginBottom: '0px',
                                                fontFamily: 'FordF1-Regular',
                                                color: '#00095B'
                                            }}>
                                                The additional contact listed below should be up-to-date on all event details and will receive important communications regarding the event.
                                            </p>
                                            <div className="contact-fields-grid">
                                                <div className="contact-column">
                                                    <Field
                                                        id={'secondary_contact_name'}
                                                        name={'secondary_contact_name'}
                                                        maxLength={100}
                                                        label={'Secondary Contact Name'}
                                                        validator={nameValidator}
                                                        required={true}
                                                        component={Input}
                                                    />
                                                </div>
                                                <div className="contact-column">
                                                    <Field
                                                        id={'secondary_contact_phone_number'}
                                                        name={'secondary_contact_phone_number'}
                                                        label={'Secondary Contact Phone'}
                                                        validator={phoneValidator}
                                                        required={true}
                                                        mask="000-000-0000"
                                                        placeholder={'e.g.: 000-000-0000'}
                                                        component={MaskedTextBox}
                                                    />
                                                </div>
                                                <div className="contact-column">
                                                    <Field
                                                        id={'secondary_contact_email'}
                                                        name={'secondary_contact_email'}
                                                        type={'email'}
                                                        maxLength={100}
                                                        placeholder={'e.g.: peter@gmail.com'}
                                                        label={'Secondary Contact Email'}
                                                        validator={emailValidator}
                                                        required={true}
                                                        component={Input}
                                                    />
                                                </div>
                                            </div>
                                            <Field
                                                id={'funding_purpose_category'}
                                                name={'funding_purpose_category'}
                                                data={[
                                                    'Community Development (Build / Restore Community Spaces)',
                                                    'Publicly Available Parks / Playgrounds / Spaces',
                                                    'Workforce Development (Vocational / Trades / Auto Tech)',
                                                    'Scholarships (Vocational / Trades / Entrepreneurship)',
                                                    'Small Business / Entrepreneurship Skill-Based Learning',
                                                    'Housing / Neighborhood Revitalization (e.g., Habitat for Humanity)',
                                                    'Hunger Relief / Food Security (e.g., Feeding America, World Central Kitchen)',
                                                    'Emergency / Disaster Preparedness, Response & Recovery (e.g.,  American Red Cross, Team Rubicon)'
                                                ]}
                                                label={localizationService.toLanguageString('custom.org_description', 'org_description')}
                                                hint={localizationService.toLanguageString('custom.org_description_hint', 'org_description_hint')}
                                                validator={requiredValidator}
                                                required={true}
                                                component={DropDownList}
                                            />
                                            <Field
                                                id={'funding_purpose'}
                                                name={'funding_purpose'}
                                                maxLength={1000}
                                                label={'Proposed Funding Purpose- Additional Details*'}
                                                hint={'Provide a description of how the funds will be used and include any relevant details. Responses must be a minimum of 275 characters.'}
                                                validator={(value) => {
                                                    const required = requiredValidator(value);
                                                    if (required) return required;
                                                    return minLengthValidator(275)(value);
                                                }}
                                                required={true}
                                                component={TextArea}
                                                rows={5}
                                            />
                                            {(() => {
                                                const currentValue = formRenderProps.valueGetter('funding_purpose') || '';
                                                const currentLength = currentValue.length;
                                                const minLength = 275;
                                                const remaining = minLength - currentLength;
                                                const isValid = currentLength >= minLength;
                                                
                                                return (
                                                    <div style={{ 
                                                        marginTop: '-10px',
                                                        marginBottom: '15px',
                                                        fontSize: '12px',
                                                        color: isValid ? '#666' : '#d32f2f',
                                                        textAlign: 'right',
                                                        paddingRight: '8px'
                                                    }}>
                                                        {currentLength} / {minLength} minimum characters
                                                        {remaining > 0 && (
                                                            <span style={{ marginLeft: '8px' }}>
                                                                ({remaining} more needed)
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            <Field
                                                id={'check_payable'}
                                                name={'check_payable'}
                                                maxLength={1000}
                                                label={'Check Payable To'}
                                                hint={'All checks will be sent to the Dealer Contact at the Dealership Address provided.'}
                                                validator={requiredValidator}
                                                required={true}
                                                component={Input}
                                            />
                                            <Field
                                                id={'kit_location'}
                                                name={'kit_location'}
                                                data={[ 'The Dealership address provided', 'The Organization address provided' ]}
                                                label={`Kit Shipment Location`}
                                                validator={requiredValidator}
                                                component={DropDownList}
                                            />
                                            <Field
                                                id={'secondary_school'}
                                                name={'secondary_school'}
                                                maxLength={100}
                                                // placeholder={'e.g.: peter@gmail.com'}
                                                label={localizationService.toLanguageString('custom.secondary_school', 'Secondary Organization Name')}
                                                required={false}
                                                component={Input}
                                                hint={`If you are working with an additional organization, please enter the name here. Otherwise, please leave this field blank.`}
                                            />
                                            <h3 className="waiver-title">Electronic Waiver Acknowledgement</h3>
                                            <p className="request-text"><span className='waiver_bold_text'>Electronic Waiver Process:</span> In an effort to continuously advance the program, all events in 2026 will be using electronic waivers to capture participant test-drive information. Please make sure you review the electronic waiver process and prepare dealership / volunteer staff. Training is <span className='waiver_bold_text'>MANDATORY</span> in order to be prepared for this process. Dealers who have not properly trained will be subject to event cancellation. By enrolling in a Drive For Your Community event, you are agreeing to utilize electronic waivers.</p>
                                            <p className="request-text">Note: <span className='waiver_bold_text'>Ford will NOT be sending devices for electronic waiver events.</span></p>
                                            <Field
                                                id={'agree_digital'}
                                                name={'agree_digital'}
                                                label=""
                                                checkboxText="I understand that my event will be assigned electronic waivers and I will NOT receive devices to administer."
                                                validator={requiredValidator}
                                                required={true}
                                                component={Checkbox}
                                            />
                                            <Field
                                                id={'agree_training'}
                                                name={'agree_training'}
                                                label=""
                                                checkboxText="I understand that I must be fully trained on the electronic waiver process or my event may be subject to cancellation."
                                                validator={requiredValidator}
                                                required={true}
                                                component={Checkbox}
                                            />
                                            <Field
                                                id={'agree_terms'}
                                                name={'agree_terms'}
                                                label={`${localizationService.toLanguageString('custom.terms', 'terms')}`}
                                                checkboxText={`${localizationService.toLanguageString('custom.agree_eligibility_terms', 'agree_eligibility_terms')}`}
                                                // hint={<a href="/terms-eligibility" target="_blank">{localizationService.toLanguageString('custom.read_eligibility_terms', 'read_eligibility_terms')}</a>}
                                                validator={requiredValidator}
                                                required={true}
                                                component={Checkbox}
                                            />
                                            <a className='terms_link' href="/terms-eligibility" target="_blank">{localizationService.toLanguageString('custom.read_eligibility_terms', 'read_eligibility_terms')}</a>
                                            <div className={'vertical_btn_container'}>
                                                <Button
                                                    primary={true}
                                                    type={'submit'}
                                                    disabled={!formRenderProps.allowSubmit || !!timeValidationError}
                                                    className='standard_btn wide'
                                                >
                                                    {localizationService.toLanguageString('custom.submitEligibility', 'submitEligibility')}
                                                </Button>
                                                <Button  className='standard_btn wide cancel' onClick={() => history.push('/events')}>
                                                    {localizationService.toLanguageString('custom.cancel', 'cancel')}
                                                </Button>
                                            </div>
                                        </FormElement>
                                    )}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <PrivacyBanner
                containerStyle={{ padding: '10px 0 0', width: '100%' }}
                privacyContainerStyle={{ padding: '10px 0', width: "60%", marginLeft: '20%' }}
                showPrivacy={true}
                language={language}
                team={'ford'}
                imgStyle={{ paddingLeft: '30px' }}
                otherLinks={[
                    { text: "CONTACT US", url: "/contact", target: '_blank' },
                    { text: "PROGRAM TERMS AND CONDITIONS", url: "/terms-eligibility", target: '_blank'},
                    { text: "PROGRAM RESOURCES ", url: "/resources", target: '_blank' },
                ]}
            />

        </>
    );
}

export default EligibilityFormScreen;
